import type { FieldValue, FieldValueExtended } from "@scayla/api-client-fetch";
import { getCountryNames } from "@scayla/core";

export const ADDRESS_FULL_SLUG = "address_full";

export const ADDRESS_FAVORITE_PREFIX = "★ ";
export const ADDRESS_SUBFIELD_SLUGS = new Set([
  "street",
  "house",
  "address_line_1",
  "address_line_2",
  "zip",
  "place",
  "country",
]);

type AddressEntry = Record<string, FieldValue | FieldValueExtended> | undefined;

export function addressEntryValue(entry: AddressEntry, key: string): string {
  const fieldValue = entry?.[key] as { value?: unknown } | undefined;
  const value = fieldValue?.value;
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return "";
}

export function addressEntryFlag(entry: AddressEntry, key: string): boolean {
  const fieldValue = entry?.[key] as { value?: unknown } | undefined;
  return fieldValue?.value === true;
}

// Maps a country code to its localized name.
export function resolveAddressCountry(code: string): string {
  if (!code) return "";
  const lang = navigator.language.split("-")[0];
  return getCountryNames(lang)[code] ?? code;
}

// Returns the ordered, non-empty display parts for one address entry: street+house,
// address line 1, address line 2, zip+city, country. Callers join these as needed
// (e.g. "\n" for multi-line history text, ", " for a single-line table cell).
export function getAddressEntryParts(entry: AddressEntry, options?: { resolveCountry?: boolean }): string[] {
  const street = [addressEntryValue(entry, "street"), addressEntryValue(entry, "house")].filter(Boolean).join(" ");
  const zipCity = [addressEntryValue(entry, "zip"), addressEntryValue(entry, "place")].filter(Boolean).join(" ");
  const countryRaw = addressEntryValue(entry, "country");
  const country = options?.resolveCountry ? resolveAddressCountry(countryRaw) : countryRaw;

  return [
    street,
    addressEntryValue(entry, "address_line_1"),
    addressEntryValue(entry, "address_line_2"),
    zipCity,
    country,
  ].filter(Boolean);
}
